import {
  LitElement,
  html,
  nothing,
} from '@lit'
import { formatMoney, t } from '@nova/utils'

class NovaCustomCalculatorValues extends LitElement {
  static properties = {
    shape: { type: String, attribute: 'shape' },
    unitType: { type: String, attribute: 'unit-type' },
    basePrice: { type: Number, attribute: 'base-price' },
    basePrice2: { type: Number, attribute: 'base-price-2' },
    basePrice3: { type: Number, attribute: 'base-price-3' },
    basePrice4: { type: Number, attribute: 'base-price-4' },
    widthRange: { type: Object, attribute: 'width-range' },
    lengthRange: { type: Object, attribute: 'length-range' },
    qty: { type: Number, attribute: 'qty' },
    isB2B: { type: Boolean, attribute: 'is-b2b', converter: (value) => value === "true" },
    b2bDiscount: { type: Number, attribute: 'b2b-discount' },
    b2bDiscountPromo: { type: Number, attribute: 'b2b-discount-promo' },
    b2bDiscountPromoStartDate: { type: String, attribute: 'b2b-discount-promo-start-date' },
    b2bDiscountPromoEndDate: { type: String, attribute: 'b2b-discount-promo-end-date' },
    d2cDiscountEnabled: { type: String, attribute: 'd2c-discount-enabled' },
    d2cDiscountStartDate: { type: String, attribute: 'd2c-discount-start-date' },
    d2cDiscountEndDate: { type: String, attribute: 'd2c-discount-end-date' },
    d2cDiscountPercentage: { type: Number, attribute: 'd2c-discount-percentage' },
    d2cDiscountPercentageRaw: { type: Number, attribute: 'd2c-discount-percentage-raw' },
    d2cDiscountEligibleSizes: { type: String, attribute: 'd2c-discount-eligible-sizes' },
    d2cDiscountTargetCategories: { type: String, attribute: 'd2c-discount-target-categories' },
    d2cProductCollectionHandles: { type: String, attribute: 'd2c-product-collection-handles' },
    catalogType: { type: String, attribute: 'catalog-type' },
    discountType: { type: String, attribute: 'discount-type' },
    catalogConfig: { type: Object, attribute: 'catalog-config' },
    minWidthError: { type: String, attribute: 'min-width-error' },
    maxWidthError: { type: String, attribute: 'max-width-error' },
    minLengthError: { type: String, attribute: 'min-length-error' },
    maxLengthError: { type: String, attribute: 'max-length-error' },
    countryCode: { type: String, attribute: 'country-code' },
    currencyCode: { type: String, attribute: 'currency-code' },
    cadMultiplier: { type: Number, attribute: 'cad-multiplier' },
  }

  connectedCallback() {
    super.connectedCallback()

    this.footToInches = 12
    this.meterToCentimeter = 100
    this.inchToCentimeter = 2.54

    this.$propWidth = document.querySelector('[name="properties[_custom_width]"]')
    this.$propLength = document.querySelector('[name="properties[_custom_length]"]')
    this.$propShape = document.querySelector('[name="properties[_custom_shape]"]')
    this.$propIsCustom = document.querySelector('[name="properties[_is_custom_size]"]')
    this.$propType = document.querySelector('[name="properties[_custom_type]"]')
    this.$propWidthShow = document.querySelector('[name="properties[Width]"]')
    this.$propLengthShow = document.querySelector('[name="properties[Length]"]')
    this.$propIsB2B = document.querySelector('[name="properties[_is_b2b]"]')
    this.$propB2BDiscount = document.querySelector('[name="properties[_b2b_discount]"]')
    this.$propB2BDiscountPromo = document.querySelector('[name="properties[_b2b_discount_promo]"]')
    this.$propB2BDiscountPromoStartDate = document.querySelector('[name="properties[_b2b_discount_promo_start_date]"]')
    this.$propB2BDiscountPromoEndDate = document.querySelector('[name="properties[_b2b_discount_promo_end_date]"]')
    this.$propB2BDiscountPromoActive = document.querySelector('[name="properties[_b2b_discount_promo_active]"]')
    this.$propD2CDiscountPercentage = document.querySelector('[name="properties[_d2c_discount_percentage]"]')
    this.$propCatalogType = document.querySelector('[name="properties[_catalog_type]"]')
    this.$propDiscountType = document.querySelector('[name="properties[_discount_type]"]')

    this.$sizeElem = document.querySelector('[name="size"]:checked')
    this.refreshPriceElements()
    this.$calculatorHost = this.closest('nova-custom-calculator')

    const initialHandles = String(this.d2cProductCollectionHandles || '')
    const pageKey = this.getD2CBaselineKey()
    const baselineMap = this.getD2CBaselineMap()
    if (baselineMap[pageKey] == null && initialHandles) {
      baselineMap[pageKey] = initialHandles
    }

    const resolvedInitialHandles = String(baselineMap[pageKey] || initialHandles || '')
    if (this.$calculatorHost && !this.$calculatorHost.dataset.d2cInitialCollectionHandles) {
      this.$calculatorHost.dataset.d2cInitialCollectionHandles = resolvedInitialHandles
    }

    this.swapSize = this.swapSize.bind(this)
    this.lastCustomPriceInfo = null
    this.pendingPriceSyncTimers = []
  }

  getD2CBaselineKey() {
    return String(window?.location?.pathname || 'unknown-page')
  }

  getD2CBaselineMap() {
    if (!window.__novaD2CBaselineCollectionsByPage) {
      window.__novaD2CBaselineCollectionsByPage = {}
    }
    return window.__novaD2CBaselineCollectionsByPage
  }

  disconnectedCallback() {
    super.disconnectedCallback()
    this.clearPendingPriceSyncTimers()
  }

  refreshPriceElements() {
    this.$priceWrapper = document.querySelector('.product__info-wrapper .js-price')
    this.$priceElems = [
      ...document.querySelectorAll(
        '.product__info-wrapper .js-product-custom-price, .product__info-wrapper .price-item--sale',
      ),
    ]
    this.$priceCompareElem = document.querySelector('.product__info-wrapper .price-item--compare')
    this.$priceCompareStandard = document.querySelector('.price-item--compare__standard')
    this.$priceCompareCustom = document.querySelector('.price-item--compare__custom')
    this.$srpCustomElem = document.querySelector('.js-product-custom-srp')
    this.$regiosCompareElem = document.querySelector('.product__info-wrapper s[data-created-by-regios-dopp]')
    this.$customPromoCompareElem = document.querySelector('.product__info-wrapper .js-custom-promo-compare')
  }

  clearPendingPriceSyncTimers() {
    this.pendingPriceSyncTimers.forEach(timer => clearTimeout(timer))
    this.pendingPriceSyncTimers = []
  }

  ensureCustomPromoCompareElem() {
    const saleContainer = this.$priceWrapper?.querySelector('.price__sale')
    if (!saleContainer) return null

    let compareElem = saleContainer.querySelector('.js-custom-promo-compare')
    if (!compareElem) {
      compareElem = document.createElement('s')
      compareElem.className = 'price-item price-item--regular js-custom-promo-compare'
      compareElem.style.textDecoration = 'line-through'
      const firstDt = saleContainer.querySelector('dt')
      if (firstDt) {
        saleContainer.insertBefore(compareElem, firstDt)
      } else {
        saleContainer.prepend(compareElem)
      }
    }
    return compareElem
  }

  queuePriceDisplayResync() {
    this.clearPendingPriceSyncTimers()
    const delays = [80, 240, 600, 1200, 2000]
    delays.forEach((delay) => {
      const timer = setTimeout(() => {
        if (!this.lastCustomPriceInfo) return
        this.refreshPriceElements()
        this.applyCustomPromoDisplay(this.lastCustomPriceInfo)
        this.syncInlineB2BPromoCompare(this.lastCustomPriceInfo)
      }, delay)
      this.pendingPriceSyncTimers.push(timer)
    })
  }

  formatPrice(cents) {
    let formatted = formatMoney(cents)
    if (this.currencyCode && this.currencyCode !== 'USD') {
      formatted += ` ${this.currencyCode}`
    }
    return formatted
  }

  applyCustomPromoDisplay(priceInfo) {
    if (!priceInfo) return
    if (this.isB2B) return
    const hasPromo = priceInfo.beforePromo > priceInfo.msrp && !this.isB2B

    if (hasPromo) {
      this.$priceWrapper?.classList.add('price--on-sale')

      if (this.$priceCompareElem) this.$priceCompareElem.classList.add('hidden')
      if (this.$priceCompareStandard) this.$priceCompareStandard.classList.add('hidden')
      if (this.$priceCompareCustom) this.$priceCompareCustom.classList.add('hidden')

      const customCompare = this.ensureCustomPromoCompareElem()
      if (customCompare) {
        customCompare.textContent = this.formatPrice(priceInfo.beforePromo)
        customCompare.style.display = ''
      }

      if (this.$regiosCompareElem) {
        this.$regiosCompareElem.style.setProperty('display', 'none', 'important')
      }
      return
    }

    if (this.$priceCompareElem) this.$priceCompareElem.classList.add('hidden')
    if (this.$regiosCompareElem) this.$regiosCompareElem.style.setProperty('display', 'none', 'important')
    if (this.$customPromoCompareElem) this.$customPromoCompareElem.style.display = 'none'
  }

  ensureSrpBubbleElem() {
    if (!this.$priceCompareStandard) return null
    let elem = document.querySelector('.js-b2b-srp-bubble')
    if (!elem) {
      elem = document.createElement('span')
      elem.className = 'price-item--compare__srp p c-grey-400 bg-grey-100 py-2xs price--msrp js-b2b-srp-bubble'
      this.$priceCompareStandard.insertAdjacentElement('afterend', elem)
    }
    return elem
  }

  syncInlineB2BPromoCompare(priceInfo) {
    const inlineCompares = [...document.querySelectorAll('.js-inline-b2b-promo-compare')]
    inlineCompares.forEach((compareEl) => compareEl.remove())

    const srpBubble = document.querySelector('.js-b2b-srp-bubble')

    if (!this.isB2B || !priceInfo || !(priceInfo.beforePromo > priceInfo.msrp)) {
      if (srpBubble) srpBubble.remove()
      return
    }

    // During an active secondary promo, showSRPForB2B() has already filled
    // $priceCompareStandard with the SRP badge; here we flip it to show the
    // wholesale price as a plain crossed-out figure (no label), and move the
    // SRP into its own badge right after it.
    if (this.$priceCompareElem) this.$priceCompareElem.classList.remove('hidden')
    if (this.$priceCompareCustom) this.$priceCompareCustom.classList.add('hidden')

    if (this.$priceCompareStandard) {
      this.$priceCompareStandard.classList.remove('hidden')
      this.$priceCompareStandard.className = 'price-item--compare__standard p c-grey-400'
      this.$priceCompareStandard.style.textDecoration = 'line-through'
      this.$priceCompareStandard.textContent = this.formatPrice(priceInfo.beforePromo)
    }

    const srpElem = this.ensureSrpBubbleElem()
    if (srpElem) {
      srpElem.textContent = `${this.formatPrice(priceInfo.price)} SRP`
    }
  }

  firstUpdated() {
    this.initialize()
  }

  updated(changedProperties) {
    if (changedProperties.has('unitType') && changedProperties.get('unitType')) {
      this.requestUpdate()

      this.initialize()
    }

    if (changedProperties.has('qty') && this.$sizeElem) {
      if (this.$sizeElem.value === 'custom-placemat' || this.$sizeElem.value === 'custom-runner') {
        this.requestUpdate()
        this.onCalculatePrice()
      }
    }
  }

  initialize() {
    this.inputFields = [...this.shadowRoot.querySelectorAll('sl-input')]
    this.inputFields.forEach((item) => {
      item.value = ''
      item.addEventListener('sl-blur', () => this.onInputChange(item))
    })

    if (this.$propWidth && this.$propWidth.value) {
      this.$propWidth.value = ''
      this.$propLength.value = ''
      this.$propShape.value = ''
      this.$propIsCustom.value = ''
      this.$propWidthShow.value = ''
      this.$propLengthShow.value = ''
      this.$propType.value = ''
      this.$propIsB2B.value = ''
      this.$propB2BDiscount.value = ''
      this.$propB2BDiscountPromo.value = ''
      this.$propB2BDiscountPromoStartDate.value = ''
      this.$propB2BDiscountPromoEndDate.value = ''
      this.$propB2BDiscountPromoActive.value = ''
      this.$propD2CDiscountPercentage.value = ''
      this.$propCatalogType.value = ''
      this.$propDiscountType.value = ''

      this.onCalculatePrice()
    }
  }

  get typeBySizeOption() {
    let type = ''
    switch (this.$sizeElem.value) {
      case 'custom-placemat':
      case 'custom-runner':
        type = 'tabletop'
        break
      case 'custom-worktop':
        type = 'worktop'
        break
      case 'custom-shag-mat':
        type = 'shag'
        break
      default:
        type = 'rug'
        break
    }

    return type
  }

  safeCeil(num, decimals = 2) {
    const factor = Math.pow(10, decimals);
    return Math.ceil(num * factor + Number.EPSILON) / factor;
  }

  get isD2CPromoEligibleSize() {
    const sizeValue = this.$sizeElem?.value || ''
    return sizeValue.startsWith('custom-')
  }

  normalizeCategory(value) {
    return String(value || '')
      .trim()
      .toLowerCase()
      .replace(/\s+/g, '-')
  }

  get isD2CPromoEligibleCategory() {
    const targets = (this.d2cDiscountTargetCategories || '')
      .split(',')
      .map(category => this.normalizeCategory(category))
      .filter(Boolean)
    if (targets.length === 0) return true

    const productCollectionHandles = (this.d2cProductCollectionHandles || '')
      .split(',')
      .map(handle => this.normalizeCategory(handle))
      .filter(Boolean)

    const baselineCollectionHandles = (this.$calculatorHost?.dataset?.d2cInitialCollectionHandles || '')
      .split(',')
      .map(handle => this.normalizeCategory(handle))
      .filter(Boolean)

    const storedBaselineCollectionHandles = (this.getD2CBaselineMap()[this.getD2CBaselineKey()] || '')
      .split(',')
      .map(handle => this.normalizeCategory(handle))
      .filter(Boolean)

    if (
      productCollectionHandles.length === 0
      && baselineCollectionHandles.length === 0
      && storedBaselineCollectionHandles.length === 0
    ) return false

    return targets.some((target) => {
      return (
        productCollectionHandles.includes(target)
        || baselineCollectionHandles.includes(target)
        || storedBaselineCollectionHandles.includes(target)
      )
    })
  }

  get effectiveD2CDiscountPercentage() {
    if (this.isB2B || !this.isD2CPromoEligibleSize || !this.isD2CPromoEligibleCategory) return 0

    const enabledRaw = String(this.d2cDiscountEnabled ?? '').toLowerCase()
    const isEnabled = enabledRaw === 'true' || enabledRaw === '1'
    if (!isEnabled) return 0

    const startDate = String(this.d2cDiscountStartDate || '').slice(0, 10)
    const endDate = String(this.d2cDiscountEndDate || '').slice(0, 10)
    if (!this.isTodayWithinInclusive(startDate, endDate)) return 0

    const pctPrimary = Number(this.d2cDiscountPercentage)
    const pctRaw = Number(this.d2cDiscountPercentageRaw)
    if (Number.isFinite(pctPrimary) && pctPrimary > 0) return pctPrimary
    if (Number.isFinite(pctRaw) && pctRaw > 0) return pctRaw
    return 0
  }

  updateDiscountDebug(priceInfo = null) {
    const debugElem = document.querySelector('.js-custom-discount-debug')
    if (!debugElem) return

    const sizeValue = this.$sizeElem?.value || ''
    const customFlag = sizeValue.startsWith('custom-')
    const categoryEligible = this.isD2CPromoEligibleCategory
    const effective = this.effectiveD2CDiscountPercentage
    const targets = this.d2cDiscountTargetCategories || '(all)'
    const productCollections = this.d2cProductCollectionHandles || '(none)'
    const baselineCollections = this.$calculatorHost?.dataset?.d2cInitialCollectionHandles || '(none)'
    const persistedCollections = this.getD2CBaselineMap()[this.getD2CBaselineKey()] || '(none)'
    const base = Number(this.d2cDiscountPercentage) || 0
    const baseRaw = Number(this.d2cDiscountPercentageRaw) || 0
    const enabledRaw = String(this.d2cDiscountEnabled ?? '').toLowerCase()
    const isEnabled = enabledRaw === 'true' || enabledRaw === '1'
    const startDate = String(this.d2cDiscountStartDate || '').slice(0, 10)
    const endDate = String(this.d2cDiscountEndDate || '').slice(0, 10)
    const dateActive = this.isTodayWithinInclusive(startDate, endDate)

    let line = `D2C DEBUG | size=${sizeValue} custom=${customFlag} categoryEligible=${categoryEligible} isB2B=${this.isB2B} enabled=${isEnabled} dateActive=${dateActive} base%=${base} raw%=${baseRaw} effective%=${effective} targets=[${targets}] collections=[${productCollections}] baselineCollections=[${baselineCollections}] persistedCollections=[${persistedCollections}]`
    if (priceInfo) {
      line += ` | before=${this.formatPrice(priceInfo.beforePromo)} after=${this.formatPrice(priceInfo.msrp)}`
    }
    debugElem.textContent = line
  }

  applyCalculatedValue(inputFields, type) {
    let calculatedValue = inputFields[0].value * this.footToInches + Number(inputFields[1].value)

    if (this.unitType === 'metric') {
      calculatedValue = (inputFields[0].value * this.meterToCentimeter + Number(inputFields[1].value)) / this.inchToCentimeter
      calculatedValue = this.safeCeil(calculatedValue)
    }

    if (type === 'w') {
      this.$propWidth.value = calculatedValue
      this.$propWidthShow.value = `${calculatedValue}${t('products.custom_calculator.units.in')}`
      if (this.shape === 'round' || this.shape === 'square') {
        this.$propLength.value = calculatedValue
        this.$propLengthShow.value = `${calculatedValue}${t('products.custom_calculator.units.in')}`
      }
    } else if (type === 'h') {
      this.$propLength.value = calculatedValue
      this.$propLengthShow.value = `${calculatedValue}${t('products.custom_calculator.units.in')}`
    }

    this.dispatchEvent(new CustomEvent('custom-calculator:values-updated', {
      detail: {
        value: calculatedValue,
        type
      }
    }))
  }

  onInputChange(item) {
    item.value = item.value.replaceAll('-', '')

    const type = item.dataset.type
    const $inputs = this.inputFields.filter(input => input.dataset.type === type)
    this.applyCalculatedValue($inputs, type)

    this.$propShape.value = this.shape
    this.$propIsCustom.value = 'true'
    this.$propType.value = this.typeBySizeOption
    this.$propIsB2B.value = this.isB2B ? 'true' : 'false'
    this.$propB2BDiscount.value = this.b2bDiscount || 0
    this.$propB2BDiscountPromo.value = this.b2bDiscountPromo || 0
    this.$propB2BDiscountPromoStartDate.value = this.b2bDiscountPromoStartDate
    this.$propB2BDiscountPromoEndDate.value = this.b2bDiscountPromoEndDate
    this.$propB2BDiscountPromoActive.value = (this.isB2B && Number(this.b2bDiscountPromo) > 0 && this.isTodayWithinInclusive(this.b2bDiscountPromoStartDate, this.b2bDiscountPromoEndDate)) ? 'true' : 'false'
    this.$propD2CDiscountPercentage.value = this.effectiveD2CDiscountPercentage
    this.$propCatalogType.value = this.catalogType || 'consumer'
    this.$propDiscountType.value = this.discountType || 'standard'


    this.onCalculatePrice()
    this.updateDiscountDebug()
    this.requestUpdate()
  }

  calculatedWidth(width) {
    let calWidth = width
    if (width) {
      switch (this.$sizeElem.value) {
        case 'custom-rug':
        case 'custom-placemat':
        case 'custom-runner':
        case 'custom-worktop':
          if (width <= 72) {
            calWidth = 72
          } else if (width <= 144) {
            calWidth = 144
          } else if (width <= 216) {
            calWidth = 216
          }
          break
        default:
          calWidth = width // fallback value
          break
      }
    }
    return calWidth
  }

  get basePriceByShape() {
    switch (this.shape) {
      case 'rectangle':
      case 'square':
        return this.basePrice
      case 'runner':
        return this.basePrice2
      case 'oval':
      case 'round':
        return this.basePrice3
      case 'special-shape':
        return this.basePrice4
    }
  }

  calculateDealerPrice(price) {
    const DEALER_OFF_SRP = 0.40 // 40% off

    if( price > 0) {
      return (price * (1 - DEALER_OFF_SRP)).toFixed(2)
    } else {
      return 0
    }
  }

  calculateMSRP(width, length) {
    const calWidth = this.calculatedWidth(width)
    const sqft = (calWidth * length) / (this.footToInches * this.footToInches)
    const pricePerSqft = this.basePriceByShape
    let price = +((pricePerSqft * sqft) * 100).toFixed(2)
    
    // Apply CAD conversion if applicable
    if (this.countryCode === 'CA' && this.cadMultiplier && this.cadMultiplier > 0) {
      price = +(price * this.cadMultiplier).toFixed(2)
    }
    
    let total = price
    // Apply B2B discount based on catalog configuration
    if (this.isB2B && this.catalogConfig) {
      const discountConfig = this.catalogConfig;
      if (discountConfig.discount_type?.value === 'compound') {
        // Compound discount (e.g., F+B Domestic: 20% + 50%)
        const firstDiscount = 1 - (discountConfig.calculator_discount / 100);
        const secondDiscount = 1 - (discountConfig.secondary_discount / 100);
        total = +(total * firstDiscount * secondDiscount).toFixed(2);
      } else if (discountConfig.discount_type?.value === 'tiered') {
        // Tiered discount based on order value (future enhancement)
        const discount = discountConfig.calculator_discount || 0;
        const discountMultiplier = 1 - (discount / 100);
        total = +(total * discountMultiplier).toFixed(2);
      }
      else {
        // Standard single discount
        const discount = discountConfig.calculator_discount || 0;
        if (discount > 0) {
          const discountMultiplier = 1 - (discount / 100);
          total = +(total * discountMultiplier).toFixed(2);
        }
      }
    }

    const beforePromo = total 

    if (this.isB2B && Number(this.b2bDiscountPromo) > 0 && this.isTodayWithinInclusive(this.b2bDiscountPromoStartDate, this.b2bDiscountPromoEndDate)) {
      const promoMultiplier = 1 - (Number(this.b2bDiscountPromo) / 100)
      total = +(total * promoMultiplier).toFixed(2)
    }

    if (this.effectiveD2CDiscountPercentage > 0) {
      const promoMultiplier = 1 - (this.effectiveD2CDiscountPercentage / 100)
      total = +(total * promoMultiplier).toFixed(2)
    }

    const dealerPrice = this.calculateDealerPrice(price)

    return {
      sqft: +sqft,
      pricePerSqft: +pricePerSqft.toFixed(2),
      price,
      beforePromo,
      msrp: total,
      b2bDiscount: this.isB2B ? this.b2bDiscount : 0,
      catalogType: this.catalogType || 'consumer',
      discountType: this.discountType || 'standard',
      dealerPrice
    }
  }

  onCalculatePrice() {
    this.$sizeElem = document.querySelector('[name="size"]:checked')
    this.refreshPriceElements()
    this.submitBtn = document.querySelector('.js-product-buy-button')
    const setPendingState = (isPending) => {
      if (!this.$priceWrapper) return
      this.$priceWrapper.classList.toggle('price--custom-pending', Boolean(isPending))
      this.$priceWrapper.classList.toggle('price--custom-ready', !Boolean(isPending))
    }

    let w = Number(this.$propWidth.value)
    let h = Number(this.$propLength.value)

    if (w && h) {
      if (this.widthRange && (this.widthRange.min > w || this.widthRange.max < w)) {
        this.$priceElems.forEach(item => item.textContent = '-')
        this.submitBtn?.setAttribute('disabled', true)
        w = 0
      }
      if (this.lengthRange && (this.lengthRange.min > h || this.lengthRange.max < h)) {
        this.$priceElems.forEach(item => item.textContent = '-')
        this.submitBtn?.setAttribute('disabled', true)
        h = 0
      }

      if (w && h) {
        if (w > h) {
          this.swapSize(w, h)
        } else {
          const priceInfo = this.calculateMSRP(w, h)
          setPendingState(false)
          
          this.showSRPForB2B(priceInfo.price)
          this.updateDealerPriceDisplay(priceInfo)
          this.submitBtn?.removeAttribute('disabled')
          this.$priceElems.forEach(item => item.textContent = this.formatPrice(priceInfo.msrp))
          this.updateDiscountDebug(priceInfo)

          this.lastCustomPriceInfo = priceInfo
          this.applyCustomPromoDisplay(priceInfo)
          this.syncInlineB2BPromoCompare(priceInfo)
          this.queuePriceDisplayResync()
        }
      } else {
        setPendingState(true)
        this.$priceElems.forEach(item => item.textContent = '-')
        if (this.$priceCompareElem) this.$priceCompareElem.classList.add('hidden')
        this.submitBtn?.setAttribute('disabled', true)
        this.lastCustomPriceInfo = null
        this.syncInlineB2BPromoCompare(null)
        this.updateDiscountDebug()
      }
    } else {
      setPendingState(true)
      this.$priceElems.forEach(item => item.textContent = '-')
      if (this.$priceCompareElem) this.$priceCompareElem.classList.add('hidden')
      this.submitBtn?.setAttribute('disabled', true)
      this.lastCustomPriceInfo = null
      this.syncInlineB2BPromoCompare(null)
      this.updateDiscountDebug()
    }

    if (this.shape === 'special-shape') {
      this.submitBtn?.setAttribute('disabled', true)
    }

    document.dispatchEvent(
      new CustomEvent('nova:product:render-custom-calculator-preview', {
        detail: {
          w,
          h,
        },
      }),
    )
  }

  updateDealerPriceDisplay(priceInfo) {
    const wrap = document.querySelector('.js-product-dealer-price-wrap')
    const el = document.querySelector('.js-product-dealer-price')
    
    if (!wrap || !el) return
    const srp = Number(priceInfo?.price || 0)
    const dealer = Number(priceInfo?.dealerPrice || 0)

    if (!(srp > 0) || !(dealer > 0)) {
      wrap.classList.add('hidden')
      wrap.setAttribute('aria-hidden', 'true')
      el.textContent = ''
      return
    }
    const label = t('products.product.price.dealer_price')
    el.textContent = label.replace('{{ price }}', this.formatPrice(dealer))
    wrap.classList.remove('hidden')
    wrap.setAttribute('aria-hidden', 'false')
  }

  showSRPForB2B(price) {
    this.refreshPriceElements()
    if (this.isB2B && this.b2bDiscount) {
      this.$priceWrapper?.classList.add(...['price--on-sale', 'price--on-msrp'])
      if (this.$priceCompareElem) this.$priceCompareElem.classList.remove('hidden')
      if (this.$priceCompareCustom) this.$priceCompareCustom.classList.add('hidden')
      if (this.$priceCompareStandard) {
        this.$priceCompareStandard.classList.remove('hidden')
        // Reset to the SRP badge baseline every time, in case a previous calculation
        // left it in the crossed-out wholesale state (see syncInlineB2BPromoCompare).
        this.$priceCompareStandard.className = 'price-item--compare__standard p c-grey-400 bg-grey-100 py-2xs price--msrp'
        this.$priceCompareStandard.style.textDecoration = ''
        this.$priceCompareStandard.textContent = `${this.formatPrice(price)} SRP`
      }
    } else {
      if (this.$priceCompareElem) this.$priceCompareElem.classList.add('hidden')
    }
  }

  swapSize(w, h) {
    const $inputsW = this.inputFields.filter(input => input.dataset.type === 'w')
    const oldW = $inputsW.map(item => item.value)
    const $inputsH = this.inputFields.filter(input => input.dataset.type === 'h')
    $inputsH.forEach((item, index) => {
      $inputsW[index].value = item.value
      item.value = oldW[index]
    })

    this.applyCalculatedValue($inputsW, 'w')
    this.applyCalculatedValue($inputsH, 'h')

    const priceInfo = this.calculateMSRP(h, w)
    this.$priceElems.forEach(item => item.textContent = this.formatPrice(priceInfo.msrp))
    this.showSRPForB2B(priceInfo.price)
    this.updateDealerPriceDisplay(priceInfo)
    this.submitBtn?.removeAttribute('disabled')

    this.lastCustomPriceInfo = priceInfo
    this.syncInlineB2BPromoCompare(priceInfo)

    this.dispatchEvent(new CustomEvent('custom-calculator:values-swapped'))
  }

  normalizeYmd(dateStr) {
    const normalized = String(dateStr || '').slice(0, 10)
    if (!/^\d{4}-\d{2}-\d{2}$/.test(normalized)) return ''
    return normalized
  }

  getTodayYmdInTimeZone(timeZone = 'America/New_York') {
    const formatter = new Intl.DateTimeFormat('en-CA', {
      timeZone,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    })
    const parts = formatter.formatToParts(new Date())
    const year = parts.find(part => part.type === 'year')?.value
    const month = parts.find(part => part.type === 'month')?.value
    const day = parts.find(part => part.type === 'day')?.value
    if (!year || !month || !day) return ''
    return `${year}-${month}-${day}`
  }

  isTodayWithinInclusive(startStr, endStr) {
    const todayYmd = this.getTodayYmdInTimeZone('America/New_York')
    if (!todayYmd) return false
    const startYmd = this.normalizeYmd(startStr)
    const endYmd = this.normalizeYmd(endStr)

    if (startYmd && endYmd) return todayYmd >= startYmd && todayYmd <= endYmd
    if (startYmd && !endYmd) return todayYmd >= startYmd
    if (!startYmd && endYmd) return todayYmd <= endYmd
    return true
  }

  renderInputs(type) {
    let firstLabel = t('products.custom_calculator.units.feet')
    let secondLabel = t('products.custom_calculator.units.inches')
    if (this.unitType === 'metric') {
      firstLabel = t('products.custom_calculator.units.meter')
      secondLabel = t('products.custom_calculator.units.centimeter')
    }

    return html`
      <div part="unit-inputs">
        <sl-input
          size="medium"
          type="number"
          min="1"
          no-spin-buttons
          required
          part="unit-input"
          data-type="${type}"
          name="${type}-1"
          autocomplete="off"
        >
          <span slot="suffix" part="unit-label">${firstLabel}</span>
        </sl-input>
        <sl-input
          size="medium"
          type="number"
          min="1"
          no-spin-buttons
          part="unit-input"
          data-type="${type}"
          name="${type}-2"
          autocomplete="off"
        >
          <span slot="suffix" part="unit-label">${secondLabel}</span>
        </sl-input>
      </div>
    `
  }

  showMinMax(range) {
    return range
      ? html`<span part="range">${t('products.custom_calculator.labels.min')} ${range.min}${t('products.custom_calculator.units.in')}, ${t('products.custom_calculator.labels.max')} ${range.max}</span>`
      : nothing
  }

  showError(range, isWidth = true) {
    const key = isWidth ? 'w' : 'h'
    this.classList.remove(`input-error-${key}-min`, `input-error-${key}-max`)

    if (range) {
      let value = Number(this.$propWidth.value)
      let minError = this.minWidthError
      let maxError = this.maxWidthError
      if (!isWidth) {
        value = Number(this.$propLength.value)
        minError = this.minLengthError
        maxError = this.maxLengthError
      }

      if (value) {
        if (Number(range.min) && Number(range.min) > value) {
          this.classList.add(`input-error-${key}-min`)
          return html`<div part="error">${minError}</div>`
        }
        if (Number(range.max) && Number(range.max) < value) {
          this.classList.add(`input-error-${key}-max`);
          return html`<div part="error">${maxError}</div>`
        }
      }
    }
    return nothing
  }

  renderFields() {
    switch (this.shape) {
      case 'rectangle':
      case 'runner':
      case 'special-shape':
      case 'oval':
        return html`
          <div class="" slot="base" part="container">
            <div part="block">
              <div part="label">
                ${t('products.custom_calculator.labels.width')}
                ${this.showMinMax(this.widthRange)}
              </div>
              ${this.showError(this.widthRange)}
              ${this.renderInputs('w')}
            </div>
            <div part="block">
              <div part="label">
                ${t('products.custom_calculator.labels.length')}
                ${this.showMinMax(this.lengthRange)}
              </div>
              ${this.showError(this.lengthRange, false)}
              ${this.renderInputs('h')}
            </div>
          </div>
        `
      case 'round':
      case 'square':
        return html`
          <div class="" slot="base" part="container">
            <div part="block">
              <div part="label">
                ${this.showMinMax(this.widthRange)}
              </div>
              ${this.showError(this.widthRange)}
              ${this.renderInputs('w')}
            </div>
          </div>
        `
      default:
        break
    }

    return nothing
  }

  render() {
    return html`
      <div part="base">
        <slot name="base"></slot>
        ${this.renderFields()}
      </div>
    `
  }
}

if (typeof customElements.get('nova-custom-calculator-values') === 'undefined') {
  customElements.define('nova-custom-calculator-values', NovaCustomCalculatorValues)
}
